import React, { useState } from 'react';
import { MegaphoneIcon, PlusIcon, SearchIcon, FilterIcon, SlidersIcon, TagIcon, CalendarIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
import { CampaignList } from '../components/campaigns/CampaignList';
import { Campaign } from '../types/Campaign';
// Mock campaign data based on screenshots
const mockCampaigns: Campaign[] = [{
  id: 'RBLSHOP2025',
  title: '[RLife] 7-days Promotion for Shop',
  description: '[RLife] 7-days Promotion for Shop',
  expiryDate: '07/10/2025',
  discountPercentage: 0,
  fixedDiscountAmount: 50,
  currency: 'LKR',
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 5000,
  budgetUsed: 0,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'RBLSHOP2025',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 50,
  maxDiscount: 50,
  status: 'active',
  companies: ['Rabbit Life'],
  transactionVolume: 0,
  transactionCount: 0
}, {
  id: 'BBL10THB',
  title: 'BBL Special Package (Rs. 10 Discount)',
  description: 'DISCOUNT_COUPON',
  expiryDate: '02/08/2025',
  discountPercentage: 0,
  fixedDiscountAmount: 10,
  currency: 'LKR',
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 11000,
  budgetUsed: 10960,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'BBL10THB',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 10,
  maxDiscount: 10,
  status: 'active'
}, {
  id: 'SUMMER2025',
  title: 'Summer Special Discount',
  description: 'Special discount for summer season',
  expiryDate: '31/08/2025',
  discountPercentage: 15,
  fixedDiscountAmount: 0,
  currency: 'LKR',
  discountCarriedBy: 'Merchant',
  merchantPercentage: 100,
  budget: 20000,
  budgetUsed: 5430,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'SUMMER2025',
  category: 'Seasonal promotion',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 100,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 200,
  status: 'active'
}];
export function Campaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>(mockCampaigns);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'expired'>('all');
  // Filter campaigns based on search and status filter
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Navigate to campaign creation page
  const navigateToCreateCampaign = () => {
    window.location.href = '/campaign-form';
  };
  // Navigate to campaign details page
  const navigateToCampaignDetails = (campaignId: string) => {
    window.location.href = `/campaign-details?id=${campaignId}`;
  };
  return <div className="space-y-6" data-id="element-3391">
      {/* Top bar with actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-3392">
        <div className="flex items-center" data-id="element-3393">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-3394">
            <MegaphoneIcon size={24} className="mr-2 text-blue-600" data-id="element-3395" />
            Campaigns
          </h1>
        </div>
        <div className="flex items-center space-x-3" data-id="element-3396">
          <button onClick={navigateToCreateCampaign} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3397">
            <PlusIcon size={16} className="mr-2" data-id="element-3398" />
            <span data-id="element-3399">Create Campaign</span>
          </button>
        </div>
      </div>
      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3400">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-3401">
          <div className="relative flex-grow" data-id="element-3402">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3403">
              <SearchIcon size={16} className="text-gray-400" data-id="element-3404" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search campaigns by title or code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3405" />
          </div>
          <div className="flex space-x-4" data-id="element-3406">
            <div className="relative" data-id="element-3407">
              <select className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-8 py-2 focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} data-id="element-3408">
                <option value="all" data-id="element-3409">All Statuses</option>
                <option value="active" data-id="element-3410">Active</option>
                <option value="inactive" data-id="element-3411">Inactive</option>
                <option value="expired" data-id="element-3412">Expired</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-3413">
                <FilterIcon size={16} className="text-gray-400" data-id="element-3414" />
              </div>
            </div>
            <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3415">
              <SlidersIcon size={16} className="text-gray-500 mr-2" data-id="element-3416" />
              <span className="text-sm" data-id="element-3417">More Filters</span>
            </button>
          </div>
        </div>
      </div>
      {/* Campaign stats summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-3418">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3419">
          <div className="flex justify-between items-start" data-id="element-3420">
            <div data-id="element-3421">
              <p className="text-sm text-gray-500 mb-1" data-id="element-3422">Total Campaigns</p>
              <h3 className="text-2xl font-bold text-gray-800" data-id="element-3423">
                {campaigns.length}
              </h3>
            </div>
            <div className="p-2 bg-blue-50 rounded-md" data-id="element-3424">
              <MegaphoneIcon size={20} className="text-blue-600" data-id="element-3425" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3426">
          <div className="flex justify-between items-start" data-id="element-3427">
            <div data-id="element-3428">
              <p className="text-sm text-gray-500 mb-1" data-id="element-3429">Active Campaigns</p>
              <h3 className="text-2xl font-bold text-gray-800" data-id="element-3430">
                {campaigns.filter(c => c.status === 'active').length}
              </h3>
            </div>
            <div className="p-2 bg-green-50 rounded-md" data-id="element-3431">
              <TagIcon size={20} className="text-green-600" data-id="element-3432" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3433">
          <div className="flex justify-between items-start" data-id="element-3434">
            <div data-id="element-3435">
              <p className="text-sm text-gray-500 mb-1" data-id="element-3436">Expiring Soon</p>
              <h3 className="text-2xl font-bold text-gray-800" data-id="element-3437">1</h3>
            </div>
            <div className="p-2 bg-orange-50 rounded-md" data-id="element-3438">
              <CalendarIcon size={20} className="text-orange-600" data-id="element-3439" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3440">
          <div className="flex justify-between items-start" data-id="element-3441">
            <div data-id="element-3442">
              <p className="text-sm text-gray-500 mb-1" data-id="element-3443">Total Budget Used</p>
              <h3 className="text-2xl font-bold text-gray-800" data-id="element-3444">
                Rs.{' '}
                {campaigns.reduce((sum, campaign) => sum + campaign.budgetUsed, 0).toLocaleString('en-LK')}
              </h3>
            </div>
            <div className="p-2 bg-purple-50 rounded-md" data-id="element-3445">
              <DollarSignIcon size={20} className="text-purple-600" data-id="element-3446" />
            </div>
          </div>
        </div>
      </div>
      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-3447">
        <CampaignList campaigns={filteredCampaigns} onCampaignClick={navigateToCampaignDetails} data-id="element-3448" />
      </div>
    </div>;
}